import { request } from './Api';
import { conf } from '../config/config';
import { recordActivity, recordEntityChange } from './ActivityService';

export async function listUsers(params = {}) {
    const query = new URLSearchParams(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
    ).toString();
    const response = await request(`/api/admin/users${query ? `?${query}` : ''}`);
    const items = Array.isArray(response.items)
        ? response.items
        : Array.isArray(response.data) ? response.data : [];

    return {
        items,
        pagination: response.pagination || null
    };
}

export async function listModels(params = {}) {
    const query = new URLSearchParams(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
    ).toString();
    const response = await request(`/api/admin/models${query ? `?${query}` : ''}`);
    const items = Array.isArray(response.items)
        ? response.items
        : Array.isArray(response.data) ? response.data : [];

    return {
        items,
        pagination: response.pagination || null
    };
}

export async function listCities() {
    const response = await request('/api/cities', { auth: false });
    return Array.isArray(response.data) ? response.data : Array.isArray(response.items) ? response.items : [];
}

export async function createUser({ name, email, password, role, status, country, model }) {
    const response = await request('/api/admin/users', {
        method: 'POST',
        body: {
            name,
            email,
            password,
            role: Number(role),
            status,
            country,
            ...(Number(role) === 2 && model ? { model } : {}),
            client_id: conf.oauth.clientId,
            client_secret: conf.oauth.clientSecret
        }
    });

    const createdUser = response.data;
    recordEntityChange('user', 1);
    recordActivity({
        type: 'user',
        action: `Created user${name ? ` "${name}"` : ''}`,
        entityType: 'user',
        entityUuid: createdUser?.uuid || createdUser?.id
    });
    if (Number(role) === 2 && model) {
        const createdModel = createdUser?.model && typeof createdUser.model === 'object' ? createdUser.model : model;
        recordEntityChange('model', 1);
        recordActivity({
            type: 'model',
            action: `Created model${createdModel?.name ? ` "${createdModel.name}"` : ''}`,
            entityType: 'model',
            entityUuid: createdModel?.uuid || createdModel?.id
        });
    }

    return createdUser;
}

export async function updateUser(uuid, changes) {
    const response = await request(`/api/admin/users/${encodeURIComponent(uuid)}`, {
        method: 'PATCH',
        body: changes
    });
    recordActivity({ type: 'user', action: 'Updated user', entityType: 'user', entityUuid: uuid });
    return response.data;
}

export async function getModel(uuid) {
    const response = await request(`/api/admin/models/${encodeURIComponent(uuid)}`);
    return response.data;
}

export async function listModelPaymentMethods(uuid) {
    const response = await request(`/api/admin/models/${encodeURIComponent(uuid)}/payment-methods`);
    return Array.isArray(response.data) ? response.data : Array.isArray(response.items) ? response.items : [];
}

export async function getModelCampaign(uuid) {
    const response = await request(`/api/admin/models/${encodeURIComponent(uuid)}/campaign`);
    return response.data || null;
}

export async function updateModelCampaign(uuid, changes) {
    const response = await request(`/api/admin/models/${encodeURIComponent(uuid)}/campaign`, {
        method: 'PATCH',
        body: changes
    });
    recordActivity({ type: 'model', action: 'Updated model campaign', entityType: 'campaign', entityUuid: response.data?.uuid || uuid });
    return response.data;
}

export async function updateModel(uuid, changes) {
    const response = await request(`/api/admin/models/${encodeURIComponent(uuid)}`, {
        method: 'PATCH',
        body: changes
    });
    recordActivity({ type: 'model', action: 'Updated model', entityType: 'model', entityUuid: uuid });
    return response.data;
}

export async function uploadModelPhoto(uuid, file) {
    const response = await request(`/api/admin/models/${encodeURIComponent(uuid)}/photos/upload-url`, {
        method: 'POST',
        body: {
            filename: file.name,
            contentType: file.type,
            size: file.size
        }
    });
    const upload = response.data;
    const uploadResponse = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': file.type,
            'Cache-Control': 'public, max-age=31536000, immutable'
        },
        body: file
    });
    if (!uploadResponse.ok) throw new Error('Unable to upload the photo to S3.');
    recordActivity({ type: 'model', action: 'Uploaded model photo', entityType: 'model', entityUuid: uuid });
    return upload.photoUrl;
}

export async function deleteUser(uuid) {
    const response = await request(`/api/admin/users/${encodeURIComponent(uuid)}`, {
        method: 'DELETE'
    });
    recordEntityChange('user', -1);
    recordActivity({ type: 'user', action: 'Deleted user', entityType: 'user', entityUuid: uuid });
    return response.data;
}

export async function deleteModel(uuid) {
    const response = await request(`/api/admin/models/${encodeURIComponent(uuid)}`, {
        method: 'DELETE'
    });
    recordEntityChange('model', -1);
    recordActivity({ type: 'model', action: 'Moved model to trash', entityType: 'model', entityUuid: uuid });
    return response.data;
}


export async function getUser(uuid) {
    const response = await request(`/api/admin/users/${encodeURIComponent(uuid)}`);
    return response.data;
}


export async function createModel({ name, city, status = 'Inactive', ...details }) {
    const response = await request('/api/admin/models', {
        method: 'POST',
        body: { ...details, name, city, status }
    });
    const createdModel = response.data;
    recordEntityChange('model', 1);
    recordActivity({
        type: 'model',
        action: `Created model${name ? ` "${name}"` : ''}`,
        entityType: 'model',
        entityUuid: createdModel?.uuid || createdModel?.id
    });
    return createdModel;
}
